import React from "react";
import { InView } from 'react-intersection-observer';
import {
  TruckIcon,
  CalendarIcon,
  UserGroupIcon,
  MapIcon,
  SupportIcon,
  ShieldCheckIcon,
  CashIcon,
} from "@heroicons/react/outline";

const KeyFeatures = () => {
  const features = [
    {
      icon: <TruckIcon className="w-10 h-10 text-white" />,
      title: "Wide Range of Vehicles",
      description: "Choose from a variety of cars, SUVs, and luxury vehicles to suit your travel needs.",
      gradient: "from-purple-500 to-pink-500",
      delay: "100"
    },
    {
      icon: <CalendarIcon className="w-10 h-10 text-white" />,
      title: "Flexible Rental Periods",
      description: "Rent a vehicle for a day, a week, or even longer with flexible rental durations.",
      gradient: "from-blue-500 to-cyan-500",
      delay: "200"
    },
    {
      icon: <UserGroupIcon className="w-10 h-10 text-white" />,
      title: "Easy Booking Process",
      description: "Book your vehicle effortlessly online with quick and secure payment options.",
      gradient: "from-green-500 to-emerald-500",
      delay: "300"
    },
    {
      icon: <MapIcon className="w-10 h-10 text-white" />,
      title: "Nationwide Coverage",
      description: "Pick up and drop off vehicles at multiple locations across the country.",
      gradient: "from-orange-500 to-red-500",
      delay: "400"
    },
    {
      icon: <SupportIcon className="w-10 h-10 text-white" />,
      title: "24/7 Customer Support",
      description: "Get assistance anytime, anywhere with our dedicated customer support team.",
      gradient: "from-indigo-500 to-purple-500",
      delay: "500"
    },
    {
      icon: <ShieldCheckIcon className="w-10 h-10 text-white" />,
      title: "Comprehensive Insurance",
      description: "All vehicles come with full insurance coverage to ensure peace of mind during your trip.",
      gradient: "from-teal-500 to-blue-500",
      delay: "600"
    },
    {
      icon: <CashIcon className="w-10 h-10 text-white" />,
      title: "Affordable Pricing",
      description: "Enjoy competitive pricing with no hidden charges and multiple payment options.",
      gradient: "from-yellow-500 to-orange-500",
      delay: "700"
    },
  ];

  return (
    <div className="py-20 bg-gradient-to-br from-gray-50 to-blue-50 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-32 h-32 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
      <div className="absolute top-0 right-0 w-32 h-32 bg-yellow-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
      <div className="absolute bottom-0 left-1/2 w-32 h-32 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
      
      <div className="container mx-auto px-4 text-center relative z-10">
        {/* Section Header */}
        <InView triggerOnce={true} threshold={0.3}>
          {({ inView, ref }) => (
            <div ref={ref} className={`mb-16 ${inView ? 'animate__animated animate__fadeInUp' : ''}`}>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-800 mb-4">
                Why Choose <span className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">Nupura Self Drive Cars?</span>
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Discover the exceptional features that make us the preferred choice for 
                reliable and comfortable vehicle rentals across the nation.
              </p>
            </div>
          )}
        </InView>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <InView key={index} triggerOnce={true} threshold={0.2}>
              {({ inView, ref }) => (
                <div
                  ref={ref}
                  className={`group relative bg-white rounded-2xl p-8 flex flex-col items-center text-center shadow-xl hover:shadow-2xl transform transition-all duration-500 ${
                    inView 
                      ? `animate__animated animate__fadeInUp animate__delay-${feature.delay}ms opacity-100 translate-y-0` 
                      : 'opacity-0 translate-y-10'
                  } hover:-translate-y-3 border border-gray-100`}
                >
                  {/* Gradient Background for Icon */}
                  <div className={`absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-gradient-to-r ${feature.gradient} rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300`}>
                    {feature.icon}
                  </div>

                  {/* Content */}
                  <div className="mt-10">
                    <h3 className="text-xl font-bold text-gray-800 mb-4 group-hover:text-gray-900 transition-colors duration-300">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed group-hover:text-gray-700 transition-colors duration-300">
                      {feature.description}
                    </p>
                  </div>

                  {/* Hover Effect Border */}
                  <div className={`absolute inset-0 rounded-2xl bg-gradient-to-r ${feature.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}></div>

                  {/* Shine Effect */}
                  <div className="absolute inset-0 rounded-2xl overflow-hidden">
                    <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white to-transparent opacity-20"></div>
                  </div>
                </div>
              )}
            </InView>
          ))}
        </div>

        {/* Bottom CTA */}
        <InView triggerOnce={true} threshold={0.3}>
          {({ inView, ref }) => (
            <div ref={ref} className={`mt-16 ${inView ? 'animate__animated animate__fadeInUp' : ''}`}>
              <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl p-8 text-white shadow-2xl">
                <h3 className="text-2xl font-bold mb-4">Ready to Experience Premium Car Rental?</h3>
                <p className="text-lg mb-6 opacity-90">
                  Join thousands of satisfied customers who trust Nupura Self Drive Cars for their travel needs.
                </p>
                <button className="bg-white text-purple-600 hover:bg-gray-100 font-bold py-3 px-8 rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300">
                  Book Your Vehicle Now
                </button>
              </div>
            </div>
          )}
        </InView>
      </div>

      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(20px, -20px) scale(1.05); }
          66% { transform: translate(-15px, 15px) scale(0.95); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob {
          animation: blob 10s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        
        /* Custom delays for animations */
        .animate__delay-100ms {
          animation-delay: 100ms;
        }
        .animate__delay-200ms {
          animation-delay: 200ms;
        }
        .animate__delay-300ms {
          animation-delay: 300ms;
        }
        .animate__delay-400ms {
          animation-delay: 400ms;
        }
        .animate__delay-500ms {
          animation-delay: 500ms;
        }
        .animate__delay-600ms {
          animation-delay: 600ms;
        }
        .animate__delay-700ms {
          animation-delay: 700ms;
        }
      `}</style>
    </div>
  );
};

export default KeyFeatures;